import { Controller } from "@hotwired/stimulus"
import { renderStreamMessage, visit } from "@hotwired/turbo"
import { acknowledgeLifecycleActivation, lifecycleActivation } from "./lifecycle_activation_acceptance"
import { signInResponse, validMultiTargetStreamResponse } from "./multi_target_revalidation_stream"

const ACCEPT = "text/vnd.turbo-stream.html, text/html;q=0.9"
const EXPECTED_STREAMS = [
  { target: "office_content", action: "update", method: null },
  { target: "wallet_hud", action: "update", method: "morph" },
  { target: "profile_affordance", action: "update", method: "morph" }
]

export default class extends Controller {
  static values = {
    timeout: { type: Number, default: 10_000 },
    url: String
  }

  connect() {
    this.desiredGeneration = 0
    this.inFlight = null
    this.pendingIntent = null
    this.acceptedLifecycleIntentId ??= null
  }

  disconnect() {
    this.invalidate()
  }

  activate(event) {
    const activation = lifecycleActivation(event, this.acceptedLifecycleIntentId)
    if (!activation) return
    if (activation.duplicate) {
      activation.acknowledge()
      return
    }
    if (this.held()) {
      if (this.inFlight) this.invalidate()
      return
    }

    const intent = { generation: ++this.desiredGeneration }
    this.pendingIntent = intent
    this.flush()
    if (this.pendingIntent === intent || this.inFlight === intent) {
      acknowledgeLifecycleActivation(this, activation)
    }
  }

  flush() {
    if (this.inFlight || !this.pendingIntent) return
    if (!this.hasUrlValue || this.held()) {
      this.pendingIntent = null
      return
    }

    const intent = this.pendingIntent
    this.pendingIntent = null
    this.start(intent)
  }

  start(intent) {
    intent.url = this.urlValue
    intent.abortController = new AbortController()
    intent.timedOut = false
    this.inFlight = intent
    this.setBusy(true)
    intent.timeoutId = window.setTimeout(() => this.timeout(intent), this.timeoutValue)

    fetch(intent.url, {
      credentials: "same-origin",
      redirect: "follow",
      headers: { Accept: ACCEPT },
      signal: intent.abortController.signal
    })
      .then((response) => this.handleResponse(response, intent))
      .catch(() => {})
      .finally(() => this.finish(intent))
  }

  async handleResponse(response, intent) {
    if (signInResponse(response)) {
      this.pendingIntent = null
      this.setBusy(false)
      visit(response.url, { action: "replace" })
      return
    }
    if (!response.ok) return

    const body = await response.text()
    if (!this.applicable(intent)) return
    if (!validMultiTargetStreamResponse(response, body, EXPECTED_STREAMS)) return
    if (!this.applicable(intent) || this.held()) return

    renderStreamMessage(body)
  }

  applicable(intent) {
    return this.inFlight === intent && !intent.timedOut &&
      intent.generation === this.desiredGeneration && this.urlValue === intent.url &&
      this.element.id === "office_content"
  }

  timeout(intent) {
    if (this.inFlight !== intent) return

    intent.timedOut = true
    intent.abortController.abort()
    this.setBusy(false)
  }

  finish(intent) {
    if (this.inFlight !== intent) return

    window.clearTimeout(intent.timeoutId)
    this.setBusy(false)
    this.inFlight = null
    this.flush()
  }

  invalidate() {
    this.desiredGeneration += 1
    this.pendingIntent = null
    if (!this.inFlight) return

    const intent = this.inFlight
    this.inFlight = null
    window.clearTimeout(intent.timeoutId)
    intent.abortController.abort()
    this.setBusy(false)
  }

  held() {
    return document.hidden || this.element.id !== "office_content" || this.element.hasAttribute("busy")
  }

  setBusy(busy) {
    if (busy) {
      this.element.dataset.activationBusy = "true"
      this.element.setAttribute("aria-busy", "true")
    } else {
      delete this.element.dataset.activationBusy
      this.element.removeAttribute("aria-busy")
    }
  }
}